/**
 * Per-company load on the pinpoint board — how much F&O each legal entity is
 * actually doing, and how much of the waiting is theirs.
 *
 * **A table at L1, deliberately** (design rules §1 asks for a justification).
 * The estate is thirty-odd legal entities and the useful question here is not
 * "which is worst" — the findings above already rank that — but "is this
 * company's number even comparable", which needs the load, the form count and
 * the percentiles side by side. Any aggregate of that is a lower-resolution
 * version of the same table. It is bounded (one scrolling pane), it sorts, and
 * every row is a door: clicking a company filters the findings above to it.
 *
 * **`case_variants` is the point of the column nobody asked for.** F&O writes
 * the legal-entity code in more than one case — `USL` and `usl` — and until the
 * fold landed on 2026-08-21 those counted as two companies, so every
 * per-company number was split between them. Measured that day: 16 of 31
 * companies arrive in two spellings. That is a data-quality finding about the
 * telemetry, and hiding it behind a clean-looking table is exactly what the
 * honesty rules forbid — so the row says how many spellings folded into it and
 * the tooltip says what that means.
 *
 * `excess_hours: 0` on a row is a **measured** zero — nothing about this company
 * cleared the floors — and prints as `0.0 h`, not as an em dash. Only a `null`
 * is unknown, and this payload has none.
 */

import { Building2, Icon, Layers } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Tooltip, Typography } from "@norican/react";

import type { FoConsoleCompanyLoad, FoConsoleLookback } from "@/lib/api";

import {
  caseVariantCaption,
  formatDurationMs,
  formatExcessHours,
  heatBand,
  lookbackOption,
} from "./console-format";
import { PanelCard } from "./panel-card";
import { PINPOINT_COMPANIES_ANCHOR } from "./params";
import { formatCount } from "./status";

interface PinpointCompaniesProps {
  companies: FoConsoleCompanyLoad[];
  lookback: FoConsoleLookback;
  /** The company the board is filtered to, or `null` for every company. */
  selected: string | null;
  /** Toggle the board's company filter. Passing the selected code clears it. */
  onSelect: (company: string | null) => void;
}

export function PinpointCompanies({
  companies,
  lookback,
  selected,
  onSelect,
}: PinpointCompaniesProps) {
  const windowCaption = lookbackOption(lookback).caption;
  const folded = companies.filter((row) => row.case_variants > 1).length;

  if (companies.length === 0) {
    return (
      <section id={PINPOINT_COMPANIES_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
        <PanelCard title="Load per company" caption={windowCaption}>
          <EmptyState
            icon={<Icon icon={Building2} size="lg" aria-hidden="true" />}
            size="sm"
            title="No company opened a form in this window"
            description="The per-company query answered and returned no row. That is a quiet window, not a broken one — a query that failed would be named in the errors above instead."
          />
        </PanelCard>
      </section>
    );
  }

  const excess = companies.map((row) => row.excess_hours);
  const excessMin = Math.min(...excess);
  const excessMax = Math.max(...excess);

  return (
    <section id={PINPOINT_COMPANIES_ANCHOR} className="flex scroll-mt-6 flex-col gap-3">
      <PanelCard
        title="Load per company"
        caption={`${formatCount(companies.length)} legal entities · ${windowCaption} · click a company to filter the findings above`}
        padding="none"
      >
        <div className="h-[28rem]">
          {/* DataGrid needs a definite height for its sticky header/scroller. */}
          <DataGrid<FoConsoleCompanyLoad>
            data={companies}
            getRowId={(row) => row.company}
            density="compact"
            aria-label="F&O form load and waiting caused, per legal entity"
            getRowSearchText={(row) => `${row.company} ${row.company_name}`}
            columns={[
              {
                id: "company",
                header: "Company",
                accessor: (row) => row.company_name,
                sortable: true,
                cell: (row) => (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="text-fg rounded text-left text-sm font-medium hover:underline focus-visible:outline-2"
                      aria-pressed={selected === row.company}
                      onClick={() => onSelect(selected === row.company ? null : row.company)}
                    >
                      {row.company_name}
                    </button>
                    <Badge tone="neutral" variant="outline" size="xs" shape="rounded">
                      {row.company}
                    </Badge>
                    <CaseVariantBadge row={row} />
                  </div>
                ),
              },
              {
                id: "users",
                header: "People",
                align: "end",
                accessor: (row) => row.users,
                sortable: true,
                cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
                footer: (rows) => (
                  <span className="tabular-nums">
                    {formatCount(rows.reduce((sum, row) => sum + row.users, 0))}
                  </span>
                ),
              },
              {
                id: "forms",
                header: "Forms",
                align: "end",
                accessor: (row) => row.forms,
                sortable: true,
                cell: (row) => <span className="tabular-nums">{formatCount(row.forms)}</span>,
              },
              {
                id: "events",
                header: "Loads",
                align: "end",
                accessor: (row) => row.events,
                sortable: true,
                cell: (row) => <span className="tabular-nums">{formatCount(row.events)}</span>,
                footer: (rows) => (
                  <span className="tabular-nums">
                    {formatCount(rows.reduce((sum, row) => sum + row.events, 0))}
                  </span>
                ),
              },
              {
                id: "p50",
                header: "Median",
                align: "end",
                accessor: (row) => row.p50_ms,
                sortable: true,
                cell: (row) => <span className="tabular-nums">{formatDurationMs(row.p50_ms)}</span>,
              },
              {
                id: "p95",
                header: "P95",
                align: "end",
                accessor: (row) => row.p95_ms,
                sortable: true,
                cell: (row) => <span className="tabular-nums">{formatDurationMs(row.p95_ms)}</span>,
              },
              {
                id: "excess",
                header: "Waiting caused",
                align: "end",
                accessor: (row) => row.excess_hours,
                sortable: true,
                cell: (row) => (
                  <span className="tabular-nums">{formatExcessHours(row.excess_hours)}</span>
                ),
                // A magnitude, so the five-step sequential ramp rather than
                // `cellTone`'s four semantic tones: the bar accelerates the scan
                // and the number stays the encoding (§3.4). The danger family is
                // the right hue here — the quantity being ranked is waiting, and
                // more of it is worse — but the bands are intensity, not a
                // verdict on the company.
                cellScale: (value) =>
                  heatBand(typeof value === "number" ? value : null, excessMin, excessMax),
                cellScaleRamp: "danger",
                footer: (rows) => (
                  <span className="tabular-nums">
                    {formatExcessHours(rows.reduce((sum, row) => sum + row.excess_hours, 0))}
                  </span>
                ),
              },
            ]}
          />
        </div>
      </PanelCard>

      <Typography variant="body-sm" tone="muted">
        {folded === 0
          ? "Every legal-entity code arrived in one spelling in this window."
          : `${formatCount(folded)} of ${formatCount(companies.length)} companies arrive with their code written in more than one case. They are folded into one row each here; before the fold each spelling counted as a company of its own, which split every per-company number between them.`}{" "}
        Waiting caused is this company&apos;s share of the ranked findings above, so a company with
        no finding shows a measured 0.0 h rather than a blank.
      </Typography>
    </section>
  );
}

function CaseVariantBadge({ row }: { row: FoConsoleCompanyLoad }) {
  const caption = caseVariantCaption(row);
  if (caption == null) {
    return null;
  }
  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        className="text-fg-muted inline-flex rounded focus-visible:outline-2"
        aria-label={`${row.company} arrives in ${row.case_variants} case spellings`}
      >
        <Badge tone="warning" variant="outline" size="xs" shape="rounded">
          <span className="inline-flex items-center gap-1">
            <Icon icon={Layers} size="xs" aria-hidden="true" />
            {row.case_variants} spellings
          </span>
        </Badge>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner side="top">
          <Tooltip.Popup>
            <span className="block max-w-xs text-xs">{caption}</span>
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
